const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");

const buildApiError = async (response) => {
  let message = `Request failed: ${response.status}`;
  try {
    const responseText = await response.text();
    try {
      const payload = JSON.parse(responseText);
      if (payload?.message) {
        message = payload.message;
      }
    } catch {
      const trimmed = responseText.trim();
      if (trimmed) {
        message = trimmed.slice(0, 220);
      }
    }
  } catch {
    // Ignore unreadable responses and keep the status-based message.
  }

  const error = new Error(message);
  error.status = response.status;
  throw error;
};

const fetchJson = async (path, options = {}) => {
  const url = `${API_BASE}${path}`;
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...options,
  });

  if (!response.ok) {
    await buildApiError(response);
  }

  return response.json();
};

const resolveApiAssetUrl = (value = "") => {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) {
    return API_BASE ? `${API_BASE}${url}` : url;
  }
  const normalized = url.replace(/^\/+/, "");
  return API_BASE ? `${API_BASE}/${normalized}` : `/${normalized}`;
};

export const submitPartnerContact = async (payload) =>
  fetchJson("/api/submit-contact.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const submitContactMessage = async (payload) =>
  fetchJson("/api/submit-message.php?saveToDb=1", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const submitVolunteerInterest = async (payload) =>
  fetchJson("/api/submit-volunteer.php?saveToDb=1", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const createVolunteer = async (payload) =>
  fetchJson("/api/create-volunteer.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const listContacts = async () => fetchJson("/api/list-contacts.php");

export const listVolunteers = async () => fetchJson("/api/list-volunteers.php");

export const listSubmissions = async () => fetchJson("/api/list-submissions.php");

export const updateSubmissionStatus = async (payload) =>
  fetchJson("/api/update-submission.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const listCategories = async () => fetchJson("/api/list-categories.php");

export const createCategory = async ({ name, description = "", parentId = null }) =>
  fetchJson("/api/create-category.php", {
    method: "POST",
    body: JSON.stringify({ name, description, parentId }),
  });

export const updateCategory = async ({ id, name, parentId }) => {
  const payload = { id, name };
  if (parentId !== undefined) {
    payload.parentId = parentId;
  }
  return fetchJson("/api/update-category.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const deleteCategory = async (id) =>
  fetchJson("/api/delete-category.php", {
    method: "POST",
    body: JSON.stringify({ id }),
  });

export const listResources = async () => fetchJson("/api/list-resources.php");

export const createResource = async (payload) =>
  fetchJson("/api/create-resource.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateResource = async (payload) =>
  fetchJson("/api/update-resource.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const deleteResource = async (id) =>
  fetchJson("/api/delete-resource.php", {
    method: "POST",
    body: JSON.stringify({ id }),
  });

export const listServices = async ({ search = "", categoryIds = [] } = {}) => {
  const params = new URLSearchParams();
  if (search) params.set("q", search);
  if (categoryIds.length) params.set("categoryIds", categoryIds.join(","));
  return fetchJson(`/api/list-services.php?${params.toString()}`);
};

export const listEvents = async ({ includeAll = false } = {}) => {
  const params = new URLSearchParams();
  if (includeAll) params.set("includeAll", "1");
  const response = await fetchJson(`/api/list-events.php${params.toString() ? `?${params.toString()}` : ""}`);
  const data = (response.data || []).map((event) => ({
    ...event,
    imageUrls: (event.imageUrls || []).map((url) => resolveApiAssetUrl(url)),
    imageUrl: resolveApiAssetUrl(event.imageUrl),
    videoUrl: resolveApiAssetUrl(event.videoUrl),
  }));
  return { ...response, data };
};

export const createEvent = async (payload) =>
  fetchJson("/api/create-event.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const submitEventSuggestion = async (payload) =>
  fetchJson("/api/submit-event.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateEvent = async (payload) =>
  fetchJson("/api/update-event.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const deleteEvent = async (id) =>
  fetchJson("/api/delete-event.php", {
    method: "POST",
    body: JSON.stringify({ id }),
  });

export const adminLogin = async ({ email, password }) => {
  const response = await fetchJson("/api/admin-login.php", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  localStorage.setItem("fvg-admin-auth", "true");
  localStorage.setItem("fvg-admin-email", response.data?.email || email);
  return response;
};

export const adminLogout = async () => {
  await fetchJson("/api/admin-logout.php", { method: "POST" });
  localStorage.removeItem("fvg-admin-auth");
  localStorage.removeItem("fvg-admin-email");
  return { success: true };
};

export const getAdminSession = async () => fetchJson("/api/admin-session.php");

export const listAdminUsers = async () => fetchJson("/api/list-admin-users.php");

export const createAdminUser = async ({ email }) =>
  fetchJson("/api/create-admin-user.php", {
    method: "POST",
    body: JSON.stringify({ email }),
  });

export const resetAdminPassword = async ({ userId }) =>
  fetchJson("/api/reset-admin-password.php", {
    method: "POST",
    body: JSON.stringify({ userId }),
  });

export const updateContactMessage = async ({ id, reviewStatus, adminNotes }) =>
  fetchJson("/api/update-contact-message.php", {
    method: "POST",
    body: JSON.stringify({ id, reviewStatus, adminNotes }),
  });

export const updateVolunteer = async ({
  id,
  name,
  email,
  phone,
  interests,
  availability,
  eventInterestType,
  preferredEventId,
  message,
  adminNotes,
  decision,
  volunteerStatus,
}) =>
  fetchJson("/api/update-volunteer.php", {
    method: "POST",
    body: JSON.stringify({
      id,
      name,
      email,
      phone,
      interests,
      availability,
      eventInterestType,
      preferredEventId,
      message,
      adminNotes,
      decision,
      volunteerStatus,
    }),
  });

export const listContactMessagesArchived = async () => fetchJson("/api/list-contact-messages-archived.php");

export const listVolunteersArchived = async () => fetchJson("/api/list-volunteers-archived.php");

export const listPublicContactInfo = async () => fetchJson("/api/list-public-contact-info.php");

export const listSupportLinks = async () => fetchJson("/api/list-support-links.php");

export const listFounderStories = async () => fetchJson("/api/list-founder-stories.php");

export const listMissionSupporters = async ({ includeInactive = false, section = "" } = {}) => {
  const params = new URLSearchParams();
  if (includeInactive) params.set("includeInactive", "1");
  if (section) params.set("section", section);
  return fetchJson(`/api/list-mission-supporters.php${params.toString() ? `?${params.toString()}` : ""}`);
};

export const createMissionSupporter = async (payload) =>
  fetchJson("/api/create-mission-supporter.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateMissionSupporter = async (payload) =>
  fetchJson("/api/update-mission-supporter.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const deleteMissionSupporter = async (id) =>
  fetchJson("/api/delete-mission-supporter.php", {
    method: "POST",
    body: JSON.stringify({ id }),
  });

export const createFounderStory = async (payload) =>
  fetchJson("/api/create-founder-story.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateFounderStory = async (payload) =>
  fetchJson("/api/update-founder-story.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const deleteFounderStory = async (id) =>
  fetchJson("/api/delete-founder-story.php", {
    method: "POST",
    body: JSON.stringify({ id }),
  });

export const updateSupportLinks = async (payload) =>
  fetchJson("/api/update-support-links.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateOrgContactInfo = async (payload) =>
  fetchJson("/api/update-org-contact-info.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const createGroupMember = async (payload) =>
  fetchJson("/api/create-group-member.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateGroupMember = async (payload) =>
  fetchJson("/api/update-group-member.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const deleteGroupMember = async (id) =>
  fetchJson("/api/delete-group-member.php", {
    method: "POST",
    body: JSON.stringify({ id }),
  });

export const assignVolunteerToEvent = async ({ eventId, volunteerId, assignedTime, role, bringItems }) =>
  fetchJson("/api/assign-volunteer-to-event.php", {
    method: "POST",
    body: JSON.stringify({ eventId, volunteerId, assignedTime, role, bringItems }),
  });

export const unassignVolunteerFromEvent = async ({ eventId, volunteerId }) =>
  fetchJson("/api/unassign-volunteer-from-event.php", {
    method: "POST",
    body: JSON.stringify({ eventId, volunteerId }),
  });

export const listEventVolunteers = async (eventId) => fetchJson(`/api/list-event-volunteers.php?eventId=${eventId}`);

export const listEventEmailLogs = async (eventId) => fetchJson(`/api/list-event-email-logs.php?eventId=${eventId}`);

export const sendEventVolunteerEmail = async ({ eventId, subject, message }) =>
  fetchJson("/api/send-event-volunteer-email.php", {
    method: "POST",
    body: JSON.stringify({ eventId, subject, message }),
  });

export const uploadEventImage = async (file) => {
  const formData = new FormData();
  formData.append("image", file);

  const url = `${API_BASE}/api/upload-event-image.php`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    await buildApiError(response);
  }

  const payload = await response.json();
  if (payload?.data?.url) {
    payload.data.url = resolveApiAssetUrl(payload.data.url);
  }
  return payload;
};

export const uploadPublicEventImage = async (file) => {
  const formData = new FormData();
  formData.append("image", file);

  const url = `${API_BASE}/api/upload-public-event-image.php`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    await buildApiError(response);
  }

  const payload = await response.json();
  if (payload?.data?.url) {
    payload.data.url = resolveApiAssetUrl(payload.data.url);
  }
  return payload;
};

const uploadEventAsset = async (file, apiPath, fieldName) => {
  const formData = new FormData();
  formData.append(fieldName, file);

  const url = `${API_BASE}${apiPath}`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    await buildApiError(response);
  }

  return response.json();
};

export const uploadEventVideo = async (file) => uploadEventAsset(file, "/api/upload-event-video.php", "video");

export const uploadPublicEventVideo = async (file) => uploadEventAsset(file, "/api/upload-public-event-video.php", "video");

export const trackAnalyticsEvent = async (payload) =>
  fetchJson("/api/track-analytics.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const listAnalyticsSummary = async ({ metric = "visits_by_page", range = "30d", from = "", to = "" } = {}) => {
  const params = new URLSearchParams();
  params.set("metric", metric);
  params.set("range", range);
  if (range === "custom") {
    if (from) params.set("from", from);
    if (to) params.set("to", to);
  }
  return fetchJson(`/api/list-analytics.php?${params.toString()}`);
};
